import checkedInOutputs from "../../amplify_outputs.json";
import workspaceLocalOutputs from "../../.amplify-workspace-local/amplify_outputs.json";
import {
  HOSTED_UI_CALLBACK_PATHS,
  HOSTED_UI_LOGOUT_PATHS,
  expandHostedUiUrls,
  GOOGLE_AUTH_ADDITIONAL_ORIGINS_ENV_KEY,
  GOOGLE_AUTH_ORIGIN_ENV_KEYS,
  isManagedGoogleAuthOrigin,
  resolveGoogleLoginHintForOrigin,
} from "../../config/googleAuthPolicy";

export const YSTNJ_AMPLIFY_OUTPUTS_PROFILE_ENV_KEY =
  "VITE_YSTNJ_AMPLIFY_OUTPUTS_PROFILE";

export type RuntimeAmplifyOutputsProfile = "hosted-dev" | "workspace-local";

export type RuntimeAmplifyOutputsEnv = {
  PROD?: boolean;
  VITE_YSTNJ_AMPLIFY_OUTPUTS_PROFILE?: string;
  YSTNJ_AUTH_ADDITIONAL_ORIGINS?: string;
  YSTNJ_DEV_ORIGIN?: string;
  YSTNJ_TAILNET_ORIGIN?: string;
};

export type RuntimeAmplifyOutputsProfileResolution = {
  requestedProfile: string | null;
  selectedProfile: RuntimeAmplifyOutputsProfile;
  validProfile: boolean;
  fallbackReason:
    | "invalid-profile"
    | "prod-workspace-local-blocked"
    | "invalid-workspace-local-outputs"
    | null;
  sourcePath: string;
};

const HOSTED_DEV_OUTPUTS_PROFILE: RuntimeAmplifyOutputsProfile = "hosted-dev";
const WORKSPACE_LOCAL_OUTPUTS_PROFILE: RuntimeAmplifyOutputsProfile =
  "workspace-local";

export const YSTNJ_PRODUCTION_RUNTIME_TUPLE = {
  appsyncUrl: "https://ocfctlfhpbafbfv6zpzsyicz24.appsync-api.us-east-2.amazonaws.com/graphql",
  userPoolId: "us-east-2_4fyCgWuBc",
  userPoolClientId: "166h8icfrmicon6hetl38bc9ic",
  identityPoolId: "us-east-2:97ab1002-5773-44d6-a7b7-a2839deadc65",
  oauthDomain: "7dd130d67f50f1a07975.auth.us-east-2.amazoncognito.com",
} as const;

const YSTNJ_PRODUCTION_AUTHORITY_ORIGINS = [
  "https://production.dl4pc4t5y4i4s.amplifyapp.com",
  "https://ystnj.intelliolabs.com",
];

const uniqueUrls = (values: string[]): string[] => {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const normalized = value.trim();
    if (!normalized || seen.has(normalized)) {
      continue;
    }
    seen.add(normalized);
    out.push(normalized);
  }
  return out;
};

const getCurrentLocation = (): Location | null => {
  if (typeof window === "undefined") {
    return null;
  }
  return window.location;
};

const normalizeOrigin = (value: string | null | undefined): string | null => {
  const normalized = String(value ?? "").trim();
  if (!normalized) {
    return null;
  }
  try {
    return new URL(normalized).origin;
  } catch {
    return null;
  }
};

const getProcessProfileEnv = (): RuntimeAmplifyOutputsEnv => {
  const maybeProcess = globalThis as typeof globalThis & {
    process?: { env?: Record<string, string | undefined> };
  };
  const profile =
    maybeProcess.process?.env?.[YSTNJ_AMPLIFY_OUTPUTS_PROFILE_ENV_KEY];
  return typeof profile === "string"
    ? { [YSTNJ_AMPLIFY_OUTPUTS_PROFILE_ENV_KEY]: profile }
    : {};
};

type ViteRuntimeImportMeta = ImportMeta & {
  readonly env: {
    readonly PROD?: boolean;
    readonly VITE_YSTNJ_AMPLIFY_OUTPUTS_PROFILE?: string;
  };
};

export const projectRuntimeAmplifyOutputsEnv = (
  env: Record<string, string | boolean | undefined> = {},
): RuntimeAmplifyOutputsEnv => {
  const projected: RuntimeAmplifyOutputsEnv = {};
  const projectedRecord = projected as Record<string, string | boolean | undefined>;
  if (typeof env.PROD === "boolean") {
    projected.PROD = env.PROD;
  }
  const requestedProfile = env[YSTNJ_AMPLIFY_OUTPUTS_PROFILE_ENV_KEY];
  if (typeof requestedProfile === "string") {
    projected[YSTNJ_AMPLIFY_OUTPUTS_PROFILE_ENV_KEY] = requestedProfile;
  }
  for (const key of GOOGLE_AUTH_ORIGIN_ENV_KEYS) {
    const value = env[key];
    if (typeof value === "string") {
      projectedRecord[key] = value;
    }
  }
  const additionalOrigins = env[GOOGLE_AUTH_ADDITIONAL_ORIGINS_ENV_KEY];
  if (typeof additionalOrigins === "string") {
    projectedRecord[GOOGLE_AUTH_ADDITIONAL_ORIGINS_ENV_KEY] = additionalOrigins;
  }
  return projected;
};

const getImportMetaRuntimeEnv = (): RuntimeAmplifyOutputsEnv =>
  projectRuntimeAmplifyOutputsEnv({
    PROD: (import.meta as ViteRuntimeImportMeta).env.PROD,
    VITE_YSTNJ_AMPLIFY_OUTPUTS_PROFILE:
      (import.meta as ViteRuntimeImportMeta).env.VITE_YSTNJ_AMPLIFY_OUTPUTS_PROFILE,
  });

const getDefaultEnv = (): RuntimeAmplifyOutputsEnv => ({
  ...getImportMetaRuntimeEnv(),
  ...getProcessProfileEnv(),
});

const toGoogleAuthEnv = (
  env: RuntimeAmplifyOutputsEnv,
): Record<string, string | undefined> =>
  Object.fromEntries(
    Object.entries(env).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );

const isValidOutputsShape = (candidate: unknown): boolean => {
  if (!candidate || typeof candidate !== "object") {
    return false;
  }
  const auth = (candidate as { auth?: { user_pool_id?: unknown; user_pool_client_id?: unknown } }).auth;
  const data = (candidate as { data?: { url?: unknown } }).data;
  return Boolean(
    auth
      && typeof auth.user_pool_id === "string"
      && auth.user_pool_id.trim()
      && typeof auth.user_pool_client_id === "string"
      && auth.user_pool_client_id.trim()
      && data
      && typeof data.url === "string"
      && data.url.trim(),
  );
};

const normalizeRequestedProfile = (
  value: string | null | undefined,
): string | null => {
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized.length > 0 ? normalized : null;
};

type RuntimeAmplifyOutputsLike = {
  auth?: {
    user_pool_id?: string;
    user_pool_client_id?: string;
    identity_pool_id?: string;
    oauth?: {
      domain?: string;
    };
  };
  data?: {
    url?: string;
  };
};

const readRuntimeOutputsTuple = (outputs: RuntimeAmplifyOutputsLike) => ({
  appsyncUrl: outputs.data?.url ?? "",
  userPoolId: outputs.auth?.user_pool_id ?? "",
  userPoolClientId: outputs.auth?.user_pool_client_id ?? "",
  identityPoolId: outputs.auth?.identity_pool_id ?? "",
  oauthDomain: outputs.auth?.oauth?.domain ?? "",
});

const isYstnjProductionAuthorityOrigin = (
  origin: string | null | undefined,
): boolean => {
  const normalizedOrigin = normalizeOrigin(origin);
  return normalizedOrigin
    ? YSTNJ_PRODUCTION_AUTHORITY_ORIGINS.includes(normalizedOrigin)
    : false;
};

export const assertRuntimeAmplifyOutputsOriginAuthority = ({
  outputs,
  origin,
}: {
  outputs: RuntimeAmplifyOutputsLike;
  origin?: string | null;
}): void => {
  if (!isYstnjProductionAuthorityOrigin(origin)) {
    return;
  }

  const actual = readRuntimeOutputsTuple(outputs);
  const mismatches = [
    ["data.url", actual.appsyncUrl, YSTNJ_PRODUCTION_RUNTIME_TUPLE.appsyncUrl],
    ["auth.user_pool_id", actual.userPoolId, YSTNJ_PRODUCTION_RUNTIME_TUPLE.userPoolId],
    [
      "auth.user_pool_client_id",
      actual.userPoolClientId,
      YSTNJ_PRODUCTION_RUNTIME_TUPLE.userPoolClientId,
    ],
    [
      "auth.identity_pool_id",
      actual.identityPoolId,
      YSTNJ_PRODUCTION_RUNTIME_TUPLE.identityPoolId,
    ],
    ["auth.oauth.domain", actual.oauthDomain, YSTNJ_PRODUCTION_RUNTIME_TUPLE.oauthDomain],
  ].filter(([, actualValue, expectedValue]) => actualValue !== expectedValue);

  if (mismatches.length === 0) {
    return;
  }

  const details = mismatches
    .map(([field, actualValue, expectedValue]) => `${field}: ${actualValue || "<missing>"} != ${expectedValue}`)
    .join("; ");
  throw new Error(
    `[auth] Production origin ${normalizeOrigin(origin)} cannot boot Amplify outputs outside the production authority tuple (${details}).`,
  );
};

export const resolveRuntimeAmplifyOutputsProfile = ({
  env = getDefaultEnv(),
  origin = getCurrentLocation()?.origin ?? null,
}: {
  env?: RuntimeAmplifyOutputsEnv;
  origin?: string | null;
} = {}): RuntimeAmplifyOutputsProfileResolution => {
  const requestedProfile = normalizeRequestedProfile(
    env[YSTNJ_AMPLIFY_OUTPUTS_PROFILE_ENV_KEY],
  );
  const defaultResolution: RuntimeAmplifyOutputsProfileResolution = {
    requestedProfile,
    selectedProfile: HOSTED_DEV_OUTPUTS_PROFILE,
    validProfile: requestedProfile === null,
    fallbackReason:
      requestedProfile === null ? null : "invalid-profile",
    sourcePath: "apps/ystnj/amplify_outputs.json",
  };
  const hostedDevResolution: RuntimeAmplifyOutputsProfileResolution = {
    requestedProfile,
    selectedProfile: HOSTED_DEV_OUTPUTS_PROFILE,
    validProfile: true,
    fallbackReason: null,
    sourcePath: "apps/ystnj/amplify_outputs.json",
  };

  if (requestedProfile === null) {
    return defaultResolution;
  }

  if (requestedProfile === HOSTED_DEV_OUTPUTS_PROFILE) {
    return hostedDevResolution;
  }

  if (requestedProfile !== WORKSPACE_LOCAL_OUTPUTS_PROFILE) {
    return defaultResolution;
  }

  if (env.PROD === true) {
    return {
      ...hostedDevResolution,
      requestedProfile,
      validProfile: true,
      fallbackReason: "prod-workspace-local-blocked",
    };
  }

  if (!isValidOutputsShape(workspaceLocalOutputs)) {
    return {
      ...hostedDevResolution,
      requestedProfile,
      validProfile: true,
      fallbackReason: "invalid-workspace-local-outputs",
    };
  }

  return {
    requestedProfile,
    selectedProfile: WORKSPACE_LOCAL_OUTPUTS_PROFILE,
    validProfile: true,
    fallbackReason: null,
    sourcePath: "apps/ystnj/.amplify-workspace-local/amplify_outputs.json",
  };
};

export const getRuntimeAmplifyOutputsProfile = ({
  env = getDefaultEnv(),
  origin = getCurrentLocation()?.origin ?? null,
}: {
  env?: RuntimeAmplifyOutputsEnv;
  origin?: string | null;
} = {}): RuntimeAmplifyOutputsProfile =>
  resolveRuntimeAmplifyOutputsProfile({ env, origin }).selectedProfile;

const selectBaseAmplifyOutputs = ({
  env,
  origin,
}: {
  env: RuntimeAmplifyOutputsEnv;
  origin: string | null | undefined;
}) => {
  if (getRuntimeAmplifyOutputsProfile({ env, origin }) === WORKSPACE_LOCAL_OUTPUTS_PROFILE) {
    return workspaceLocalOutputs;
  }
  return checkedInOutputs;
};

const getManagedOriginRuntimeRedirects = (
  origin: string | null | undefined,
  env: RuntimeAmplifyOutputsEnv,
): string[] => {
  if (!origin) {
    return [];
  }
  const googleAuthEnv = toGoogleAuthEnv(env);
  if (!isManagedGoogleAuthOrigin(origin, googleAuthEnv)) {
    return [];
  }
  return expandHostedUiUrls(HOSTED_UI_CALLBACK_PATHS, googleAuthEnv);
};

export const getRuntimeAmplifyOutputs = ({
  origin = getCurrentLocation()?.origin ?? null,
  env = getDefaultEnv(),
}: {
  origin?: string | null;
  env?: RuntimeAmplifyOutputsEnv;
} = {}) => {
  const runtimeOutputs = JSON.parse(
    JSON.stringify(selectBaseAmplifyOutputs({ env, origin })),
  ) as typeof checkedInOutputs;
  assertRuntimeAmplifyOutputsOriginAuthority({ outputs: runtimeOutputs, origin });
  const oauth = runtimeOutputs.auth?.oauth;
  if (!oauth) {
    return runtimeOutputs;
  }

  const runtimeRedirects = getManagedOriginRuntimeRedirects(origin, env);
  if (runtimeRedirects.length === 0) {
    return runtimeOutputs;
  }

  const googleAuthEnv = toGoogleAuthEnv(env);
  oauth.redirect_sign_in_uri = uniqueUrls([
    ...(oauth.redirect_sign_in_uri ?? []),
    ...runtimeRedirects,
  ]);
  oauth.redirect_sign_out_uri = uniqueUrls([
    ...(oauth.redirect_sign_out_uri ?? []),
    ...expandHostedUiUrls(HOSTED_UI_LOGOUT_PATHS, googleAuthEnv),
  ]);
  return runtimeOutputs;
};

const originMatches = (
  urls: string[],
  origin: string | null | undefined,
): boolean => {
  if (!origin) {
    return false;
  }
  return urls.some((value) => {
    try {
      return new URL(value).origin === origin;
    } catch {
      return false;
    }
  });
};

export const hasGoogleOauthProvider = ({
  origin = getCurrentLocation()?.origin ?? null,
  env = getDefaultEnv(),
}: {
  origin?: string | null;
  env?: RuntimeAmplifyOutputsEnv;
} = {}): boolean => {
  const oauth = getRuntimeAmplifyOutputs({ origin, env }).auth?.oauth;
  if (!oauth?.domain || !Array.isArray(oauth.identity_providers)) {
    return false;
  }
  return oauth.identity_providers.some(
    (provider) => provider.toUpperCase() === "GOOGLE",
  );
};

export const isOriginSupportedForOauth = (
  origin: string | null | undefined,
  {
    env = getDefaultEnv(),
  }: {
    env?: RuntimeAmplifyOutputsEnv;
  } = {},
): boolean => {
  const oauth = getRuntimeAmplifyOutputs({ origin, env }).auth?.oauth;
  if (!oauth) {
    return false;
  }
  return (
    originMatches(oauth.redirect_sign_in_uri ?? [], origin) &&
    originMatches(oauth.redirect_sign_out_uri ?? [], origin)
  );
};

export const isCurrentOriginSupportedForOauth = (): boolean =>
  isOriginSupportedForOauth(getCurrentLocation()?.origin);

export const getGoogleLoginHintForCurrentOrigin = (): string | null =>
  resolveGoogleLoginHintForOrigin(
    getCurrentLocation()?.origin,
    toGoogleAuthEnv(getDefaultEnv()),
  );
